import { Dispatch, SetStateAction, FC, ReactNode, useState } from 'react';

import { useChat } from '@eneco/components/chat/useChat';
import SearchChatBotDialogWrapper from '@eneco/custom-components/SearchChatBotDialogWrapper';
import { Box, Button, Form, Grid, Text, IconButton, Stack } from '@eneco/sparky';
import { StyledInput } from '@eneco/sparky/components/Input/Input';
import { CloseIcon, SearchIcon } from '@eneco/sparky/icons';
import { useI18nTranslation } from '@eneco/sparky/providers';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { SearchChatBotRendering } from '@sitecore/types/SearchChatBot';
import { SubmitHandler, useForm } from 'react-hook-form';

import { usePlaceholderComponentContent } from '../hooks/usePlaceholderComponentContent';

interface FormFields {
  question?: string;
}

interface SearchChatBotProps {
  platform: 'mobile' | 'desktop';
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

interface SearchChatBotFormProps {
  closeButton?: ReactNode;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

const SearchChatBotForm: FC<SearchChatBotFormProps> = ({ closeButton, setIsOpen }) => {
  const [mount] = useChat();
  const { register, handleSubmit, reset } = useForm<FormFields>();
  const content = usePlaceholderComponentContent<SearchChatBotRendering>('jss-main-navigation-right', 'SearchChatBot');

  const submitForm: SubmitHandler<FormFields> = ({ question }) => {
    mount({
      visibility: 'open',
      askText: question,
      topic: content?.fields.defaultChatTopic.value,
      sendTopicAndAskText: true,
    });
    setIsOpen && setIsOpen(false);
    reset();
  };
  if (!content?.fields) return null;
  const { fields } = content;

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <Grid gap={6} columns={2}>
        <Grid.Item gridColumn={'1/-1'}>
          <Stack direction={'row'} alignY={'end'} alignX={'justify'}>
            <Text size="QuoteM" weight="bold">
              {fields.title.value}
            </Text>
            {closeButton}
          </Stack>
        </Grid.Item>

        <Grid.Item
          gridColumn={{
            initial: '1/-1',
            md: '1',
          }}>
          <StyledInput {...register('question', { maxLength: 75 })} placeholder={fields.placeholderText.value} />
        </Grid.Item>

        <Grid.Item gridColumn={'1/-1'}>
          <Box>
            <Button type={'submit'}>{fields.buttonText.value}</Button>
          </Box>
        </Grid.Item>
      </Grid>
    </Form>
  );
};

const SearchChatBot: FC<SearchChatBotProps> = ({ platform, setIsOpen }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const closeText = useI18nTranslation('close');
  const content = usePlaceholderComponentContent<SearchChatBotRendering>('jss-main-navigation-right', 'SearchChatBot');

  if (!content?.fields) return null;

  if (platform === 'mobile') {
    return (
      <Box paddingY={4} paddingX={8}>
        <SearchChatBotForm setIsOpen={setIsOpen} />
      </Box>
    );
  }

  return (
    <SearchChatBotDialogWrapper
      trigger={
        <IconButton label={content?.fields.searchIconAriaText.value || ''}>
          <SearchIcon></SearchIcon>
        </IconButton>
      }
      isOpen={isDialogOpen}
      setOpen={setIsDialogOpen}>
      <SearchChatBotForm
        closeButton={
          <DialogPrimitive.Close asChild>
            <IconButton label={closeText}>
              <CloseIcon />
            </IconButton>
          </DialogPrimitive.Close>
        }
        setIsOpen={setIsDialogOpen}></SearchChatBotForm>
    </SearchChatBotDialogWrapper>
  );
};

export default SearchChatBot;
